import AnchorPositionObserver from "../interfaces/AnchorPositionObserver";
import { createElement as h } from "../utils";

export default class Tooltip extends HTMLElement {
  /**
   * Define the element in the document's custom element registry.
   *
   * @param {string} [tag] The tag to use in the element definition
   */
  static define(tag = "ifrs-tooltip") {
    if (!customElements.get(tag)) {
      customElements.define(tag, this);
    }
  }

  constructor() {
    super();
    this.cleanup = new Set();
    this.positionObserver = new AnchorPositionObserver();
  }

  /**
   * A collection for side effect cleanup functions. Each function in the set
   * is called and removed when the component is disconnected.
   *
   * @type {Set<() => void>}
   */
  cleanup;

  /**
   * The current state of the component.
   *
   * @type {boolean}
   */
  get open() {
    return this.hasAttribute("open");
  }

  /**
   * @param {boolean} value The new state of the component.
   */
  set open(value) {
    this.toggleAttribute("open", !!value);
  }

  /** @type {AnchorPositionObserver} */
  positionObserver;

  /**
   * Reacts to changes of observed attributes. The function makes DOM updates
   * to reflect the current state of the component.
   *
   * @param {string} name The changed attribute name
   * @param {*} prev The previous value of the attribute
   * @param {*} next The new value of the attribute
   */
  async attributeChangedCallback(name, prev, next) {
    // Toggle component state when the `open` attribute changes.
    if (name === "open") {
      const description = this.querySelector(".tooltip-description");

      // Abort if we don't have the expected markup.
      if (!(description instanceof HTMLElement)) {
        return;
      }

      // Get the new state of the component.
      const isOpen = next !== null;

      // Toggle the description element visibility
      description.hidden = !isOpen;

      if (isOpen) {
        this.positionObserver.observe(this, description);
      } else {
        this.positionObserver.disconnect();
      }
    }
  }

  /**
   * Set up the component once it has been added to the DOM.
   */
  connectedCallback() {
    // Exit early if the component is not connected.
    if (!this.isConnected) {
      return;
    }

    // Set up component markup
    this.setup();

    const handleMouseOver = () => (this.open = true);
    this.addEventListener("mouseover", handleMouseOver);
    this.cleanup.add(() =>
      this.removeEventListener("mouseover", handleMouseOver),
    );

    const handleMouseOut = () => (this.open = false);
    this.addEventListener("mouseout", handleMouseOut);
    this.cleanup.add(() =>
      this.removeEventListener("mouseout", handleMouseOut),
    );
  }

  setup() {
    this.tabIndex = 0;

    if (
      this.hasAttribute("description") &&
      !this.querySelector(".tooltip-description")
    ) {
      const description = h(
        "span",
        { className: "tooltip-description", hidden: true },
        this.getAttribute("description"),
      );
      this.append(description);
      this.removeAttribute("description");
    }
  }

  /**
   * Clean up side effects when the component is disconnected.
   */
  disconnectedCallback() {
    // Call and delete all cleanup functions
    if (this.cleanup) {
      this.cleanup.forEach((fn) => fn());
      this.cleanup.clear();
    }
  }
}

Object.assign(Tooltip, { observedAttributes: ["open"] });
